// Apolo IDE — E2 (Camada V): telemetria de turno do agent loop.
//
// O doc "Anatomia do Claude" (5.3): "inteligência aparente = ... loop
// disciplinado". Disciplina precisa ser MEDIDA. Este helper PURO monta 1
// registro por turno a partir do que o SessionWorkspace já tem (sem tocar o
// agentLoop / hot path): modelo, nº de iterações, stopReason, nº de tools,
// duração. Vai pro session_events via observe('turn_telemetry', rec) — efêmero,
// sem prompt cru, só métricas. Reusa o canal de observação já existente.

import { findModel } from './modelRegistry.js';
import { buildRulesInjection } from './agentRulesPrefs.js';

// E2-WS2-T01 — normalize a provider usage object → { tokensIn, tokensOut }.
// Handles OpenAI-compat (prompt/completion) and Anthropic (input/output) shapes.
function normalizeUsage(usage) {
  if (!usage || typeof usage !== 'object') return { tokensIn: 0, tokensOut: 0 };
  return {
    tokensIn: Number(usage.input_tokens ?? usage.prompt_tokens ?? 0) || 0,
    tokensOut: Number(usage.output_tokens ?? usage.completion_tokens ?? 0) || 0,
  };
}

// E2-WS2-T01 — price a LOCAL turn's usage via the registry for its model id.
// Local Ollama/LMStudio entries price at $0 (free compute) — the row still
// records the TOKENS so the Custos card reflects real local throughput; a local
// id that proxies a priced model (or an OpenRouter-reported usage.cost) yields a
// real number. Pure; never throws.
export function priceTurnUsage(usage, modelId) {
  if (!usage || typeof usage !== 'object') return 0;
  const reported = Number(usage.cost ?? usage.total_cost);
  if (Number.isFinite(reported) && reported >= 0) return reported;
  const entry = findModel(modelId);
  if (!entry) return 0;
  const { tokensIn, tokensOut } = normalizeUsage(usage);
  const inP = entry.pricing?.input ?? 0;
  const outP = entry.pricing?.output ?? 0;
  return (tokensIn * inP + tokensOut * outP) / 1_000_000;
}

/**
 * buildTurnTelemetry({ model, agentResult, toolCount, startedAt, now }) → rec
 * Campos ausentes degradam pra null/0 (nunca lança). `now` injetável p/ teste.
 * E2-WS2-T01 — também fold o `usage` do turno LOCAL (threaded por runLocalAgentLoop)
 * em tokensIn/tokensOut + costUsd (precificado via registry) pro card de Custos.
 */
// E2-WS3-T01 — erro capped p/ persistência (helper: mantém buildTurnTelemetry
// abaixo do teto de cognitive-complexity do ratchet).
const shaOf = (r) => (typeof r?.headSha === 'string' ? r.headSha : null);

function capError(e) {
  return typeof e === 'string' && e ? e.slice(0, 500) : null;
}

export function buildTurnTelemetry({ model, agentResult, toolCount, startedAt, now = Date.now() } = {}) {
  const r = agentResult && typeof agentResult === 'object' ? agentResult : {};
  const start = typeof startedAt === 'number' ? startedAt : null;
  const modelId = typeof model === 'string' && model ? model : null;
  const { tokensIn, tokensOut } = normalizeUsage(r.usage);
  return {
    model: modelId,
    // Correlação Langfuse-style (DeerFlow follow-up): turnId = String(startedAt) — o
    // MESMO id que o recordTurnTrace usa (turn_traces.turn_id). Antes o turn_telemetry
    // não tinha turnId: numa sessão com N turnos, telemetria↔trace só casava por
    // proximidade de timestamp (frágil). Agora é join exato.
    turnId: start != null ? String(start) : null,
    ok: r.ok === true,
    iterations: Number.isFinite(r.iterations) ? r.iterations : 0,
    stopReason: typeof r.stopReason === 'string' ? r.stopReason : null,
    // FASE-11 E2-WS3-T01 — causa-raiz do turno falho persiste na telemetria (capped;
    // antes ok:false chegava sem NENHUM porquê — indiagnosticável cross-session).
    error: capError(r.error),
    toolCount: Number.isFinite(toolCount) ? toolCount : 0,
    durationMs: start != null ? Math.max(0, now - start) : null,
    // New cost fields — additive; existing consumers ignore them.
    tokensIn,
    tokensOut,
    costUsd: priceTurnUsage(r.usage, modelId),
    headSha: shaOf(r), // Dente #4 — SHA do worktree no fim do run
    // verify-loop-observability E2 — fold the per-turn observability signal so it
    // PERSISTS to session_events (the learning loop reads it cross-session via
    // patternDetector). Bounded SCALARS only — the localized weak stage + the RAG
    // usage scores — never raw console/chunks. null when the loop didn't produce them.
    weakestStage: r.trace && r.trace.weakestStage ? r.trace.weakestStage.stage : null,
    traceOk: r.trace ? r.trace.ok === true : null,
    retrievalUsedPct: r.ragQuality ? r.ragQuality.retrievalUsedPct : null,
    faithfulness: r.ragQuality ? r.ragQuality.faithfulness : null,
    ragTotal: r.ragQuality ? r.ragQuality.total : null,
    // F13-E8-T01 — drop de livros OBSERVÁVEL em runtime (counts-only): quantos
    // livros ATIVOS ficaram fora da doutrina deste turno (orçamento/sem texto).
    // Antes o dropped[] só aparecia na UI de Settings — invisível pro learning.
    droppedBooks: droppedBooksCount(),
  };
}

// E8-T01 — mesma seleção de prefs que o turno usou; best-effort (erro ⇒ 0).
function droppedBooksCount() {
  try { return (buildRulesInjection()?.dropped || []).length; } catch { return 0; }
}
